import React, { useCallback, useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";

import {
  Action,
  ActionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import { regionValuesArr } from "../../api/index";
import { actionTypeToString } from "../../constants/periscope-actions";
import { difference } from "../../helpers/app-settings-helpers";
import mainStyles from "../../styles";
import ConfirmDialog from "../shared/confirm-dialog";
import DisplayError from "../shared/display-error";
import DisplayHeading from "../shared/display-heading";
import settingsStyles from "./styles";
import {
  addAction,
  listAllActions,
  removeAction,
} from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";

const ActionAppSettings = () => {
  const classes = mainStyles();
  const settingsClasses = settingsStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [unallocatedActions, setUnallocatedActions] = useState<Action[]>([]);
  const [activeActions, setActiveActions] = useState<Action[]>([]);
  const [selectedActionType, setSelectedActionType] = useState<ActionType>();
  const [deleteActionType, setDeleteActionType] = useState<ActionType>();
  const [labelDeleteActionType, setLabelDeleteActionType] =
    useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");

  const getAllActionsReq = async () => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return listAllActions(callOptionsHeaders(regionCode))
          .then((actionsListResp) => {
            const available = Object.keys(ActionType).map((key) => {
              const action = new Action({
                actionType: ActionType[key as keyof typeof ActionType],
              });
              return action;
            });
            setUnallocatedActions(difference(available, actionsListResp));
            setActiveActions(actionsListResp);
            setErrorMessage("");
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      })
    );
    setIsLoading(false);
  };

  const addActionReq = async (actionType: ActionType) => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return addAction({ actionType }, callOptionsHeaders(regionCode))
          .then(() => {
            getAllActionsList();
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      })
    );
    setIsLoading(false);
  };

  const removeActionReq = async (actionType: ActionType) => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return removeAction({ actionType }, callOptionsHeaders(regionCode))
          .then(() => {
            getAllActionsList();
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      })
    );
    setIsLoading(false);
  };

  const getAllActionsList = useCallback(getAllActionsReq, []);
  useEffect(() => {
    getAllActionsList();
  }, [getAllActionsList]);

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      alignItems="center"
      direction="column"
      sx={classes.root}
    >
      <DisplayError errorMessage={errorMessage} />
      <Grid item xs={12}>
        <Box
          component="form"
          sx={settingsClasses.formSpacing}
          noValidate
          autoComplete="off"
        >
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="flex-start"
              gap={2}
              flexWrap="wrap"
              justifyContent="center"
            >
              <FormControl
                variant="outlined"
                size="small"
                sx={classes.formControl}
              >
                <InputLabel id="select-action-label">Action</InputLabel>
                <Select
                  labelId="select-action-label"
                  label="Action"
                  value={selectedActionType as ActionType}
                  onChange={(event) =>
                    setSelectedActionType(event.target.value as ActionType)
                  }
                  sx={classes.selector}
                  disabled={unallocatedActions.length === 0}
                >
                  {unallocatedActions.map((action) => {
                    const actionName = actionTypeToString(action.actionType);
                    if (actionName === "Action not specified") {
                      return null;
                    }
                    return (
                      <MenuItem
                        value={action.actionType}
                        key={action.actionType}
                      >
                        {actionTypeToString(action.actionType)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {isLoading ? (
                <CircularProgress
                  color="primary"
                  variant="indeterminate"
                  thickness={3}
                  size={30}
                  sx={settingsClasses.smallLoadingSpinner}
                />
              ) : (
                <Button
                  disabled={unallocatedActions.length === 0}
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ minHeight: "40px" }}
                  onClick={() => {
                    addActionReq(selectedActionType as ActionType);
                  }}
                >
                  Add Action
                </Button>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <DisplayHeading>Active Actions:</DisplayHeading>
          </Grid>
          <Grid item xs={12}>
            {isLoading ? (
              <CircularProgress
                color="primary"
                sx={settingsClasses.loadingSpinner}
                thickness={1}
                size={90}
              />
            ) : (
              <List
                sx={settingsClasses.actionsList}
                dense
                component="div"
                role="list"
              >
                {activeActions.map((action: Action, index) => {
                  const actionType = action.actionType;
                  const actionName = actionTypeToString(actionType);
                  if (actionName == "Action not specified") {
                    return null;
                  }
                  return (
                    <ListItem
                      key={`active-actions-item-${actionType}-${index}`}
                      role="listitem"
                    >
                      <ListItemText
                        id={`active-actions-item-${actionType}-label`}
                        primary={actionTypeToString(actionType)}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => {
                            setDeleteActionType(actionType as ActionType);
                            setLabelDeleteActionType(
                              actionTypeToString(actionType)
                            );
                            setOpenConfirmDialog(true);
                          }}
                          size="large"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
                <ListItem />
              </List>
            )}
          </Grid>
        </Box>
      </Grid>

      <ConfirmDialog
        title="Confirm Action"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          // Unlikely to be empty but validation requires it
          if (deleteActionType) {
            removeActionReq(deleteActionType);
          }
        }}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        Are you sure you want to delete Action - {labelDeleteActionType}?
      </ConfirmDialog>
    </Grid>
  );
};

export default ActionAppSettings;
